import { baseApi } from './baseApi';
import { saveAs } from 'file-saver';
import { getOidcToken } from '../auth/tokenManager';
import { getSignedRequest } from '../auth/awsCredentials';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';
import { normalizeSchemaVersions } from '../../utils/general/schemaVersions';
import { getSchemaEndpointSegment } from '../../utils/general/schemaEntity';

const BASE_API = process.env.REACT_APP_BASE_API;
const _baseUrl = new URL(BASE_API);
const HOSTNAME = _baseUrl.hostname;
const BASE_PATH = _baseUrl.pathname.replace(/\/+$/, '');
const REGION = process.env.REACT_APP_AWS_REGION;
const IDENTITY_POOL_ID = process.env.REACT_APP_IDENTITY_POOL_ID;
const USER_POOL_ID = process.env.REACT_APP_USER_POOL_ID;

/**
 * Perform a signed API call outside of the normal baseQuery flow.
 * Used by queryFn endpoints that need to make the signed call themselves
 * (e.g. two-step fetch: get signed URL then fetch from S3).
 */
async function signedFetch(method, path, payload) {
  const idToken = getOidcToken();
  if (!idToken) throw new Error('Missing idToken');

  const creds = await getSignedRequest(idToken, REGION, IDENTITY_POOL_ID, USER_POOL_ID);

  const signer = new SignatureV4({
    credentials: creds,
    service: 'execute-api',
    region: REGION,
    sha256: Sha256,
  });

  const fullPath = `${BASE_PATH}${path}`;
  const urlObj = new URL(`https://${HOSTNAME}${fullPath}`);
  const queryParams = Object.fromEntries(urlObj.searchParams.entries());

  const request = new HttpRequest({
    method,
    protocol: 'https:',
    hostname: HOSTNAME,
    path: urlObj.pathname,
    query: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    headers: { host: HOSTNAME, 'Content-Type': 'application/json' },
    body: payload ? JSON.stringify(payload) : undefined,
  });

  const signed = await signer.sign(request);

  const fullUrl =
    `${signed.protocol}//${signed.hostname}${signed.path}` +
    (signed.query
      ? '?' +
        Object.entries(signed.query)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join('&')
      : '');

  const response = await fetch(fullUrl, {
    method: signed.method,
    headers: signed.headers,
    body: signed.body,
  });

  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data?.detail || JSON.stringify(data));
    err.status = response.status;
    throw err;
  }
  return data;
}

export const datagroupsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── List all data groups ──────────────────────────────────────────
    getDataGroups: builder.query({
      query: () => '/datagroups',
      transformResponse: (response) => response?.dataGroups || [],
      providesTags: ['DataGroups'],
    }),

    // ── List releases for a group ────────────────────────────────────
    getReleases: builder.query({
      query: ({ groupName = 'Lab', includeDeprecated = false } = {}) => {
        const encoded = encodeURIComponent(groupName);
        // Backend expects the snake_case query param `include_deprecated`
        // (FastAPI Query params are not camel-aliased).
        return includeDeprecated
          ? `/datagroups/${encoded}/releases?include_deprecated=true`
          : `/datagroups/${encoded}/releases`;
      },
      providesTags: ['Releases'],
    }),

    // ── Deprecate a release (dry-run preview or execute) ─────────────
    deprecateRelease: builder.mutation({
      query: ({ groupName, releaseVersion, payload, dryRun = false }) => {
        const encoded = encodeURIComponent(groupName);
        const encodedVersion = encodeURIComponent(releaseVersion);
        const url = `/datagroups/${encoded}/releases/${encodedVersion}/deprecate${
          dryRun ? '?dryRun=true' : ''
        }`;
        return { url, method: 'POST', body: payload };
      },
      // Only invalidate the cached list when we actually execute (not on dry-run)
      invalidatesTags: (result, error, { dryRun }) => (dryRun ? [] : ['Releases', 'Untagged']),
    }),

    // Hard delete the latest release (DELETE /datagroups/{g}/releases/{v}).
    // Supports a dry-run preview; the backend rejects non-latest releases (400).
    hardDeleteRelease: builder.mutation({
      query: ({ groupName, releaseVersion, payload, dryRun = false }) => {
        const encoded = encodeURIComponent(groupName);
        const encodedVersion = encodeURIComponent(releaseVersion);
        const url = `/datagroups/${encoded}/releases/${encodedVersion}${
          dryRun ? '?dryRun=true' : ''
        }`;
        return { url, method: 'DELETE', body: payload };
      },
      // Only invalidate the cached list when we actually execute (not on dry-run)
      invalidatesTags: (result, error, { dryRun }) => (dryRun ? [] : ['Releases', 'Untagged']),
    }),

    // ── Get group data (events/resources/subGroups) for a release ────
    // Returns every entity in the group with its full subgroup chain in
    // `parent`, so the sidebar filters this payload client-side rather than
    // fetching per subgroup.
    getGroupData: builder.query({
      query: ({ groupName, releaseVersion, latest }) => {
        const encoded = encodeURIComponent(groupName);
        if (latest) return `/datagroups/${encoded}?latest=true`;
        return `/datagroups/${encoded}?releaseVersion=${releaseVersion}`;
      },
      providesTags: (result, error, { groupName }) => [{ type: 'GroupData', id: groupName }],
    }),

    // ── Get untagged events/resources/complexDataTypes ────────────────
    getUntagged: builder.query({
      query: ({ groupName }) => `/datagroups/${encodeURIComponent(groupName)}/untagged`,
      providesTags: ['Untagged'],
    }),

    // ── Create a new release ─────────────────────────────────────────
    createRelease: builder.mutation({
      query: ({ groupName, payload }) => ({
        url: `/datagroups/${encodeURIComponent(groupName)}/release`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Releases', 'Untagged'],
    }),

    // ── Get entity schema ────────────────────────────────────────────
    getEntitySchema: builder.query({
      query: ({ groupName, type, parent, name, version }) => {
        const encoded = encodeURIComponent(groupName);
        if (type === 'complexDataType') {
          return `/datagroups/${encoded}/complexdatatypes/${encodeURIComponent(
            name
          )}/schema?version=${encodeURIComponent(version)}`;
        }
        const entity = getSchemaEndpointSegment(type);
        if (!entity) throw new Error(`Unsupported schema entity type: ${type}`);
        const encodedParent = (parent || '').split('/').map(encodeURIComponent).join('/');
        return `/datagroups/${encoded}/subgroups/${encodedParent}/${entity}/${encodeURIComponent(
          name
        )}/schema?version=${encodeURIComponent(version)}`;
      },
      providesTags: (result, error, { name, version }) => [
        { type: 'Schema', id: `${name}-${version}` },
      ],
    }),

    // ── List versions for a specific entity ──────────────────────────
    // `list` selects what to fetch: 'releaseVersion' (default, full-featured
    // groups) or 'version' (latest-only groups like DiabetesCare).
    getReleaseVersionsForEntity: builder.query({
      query: ({ groupName, type, parent, name, list = 'releaseVersion' }) => {
        const encoded = encodeURIComponent(groupName);
        if (type === 'complexDataType') {
          return `/datagroups/${encoded}/complexdatatypes/${encodeURIComponent(name)}?list=${list}`;
        }
        const entity = getSchemaEndpointSegment(type);
        if (!entity) throw new Error(`Unsupported schema entity type: ${type}`);
        const encodedParent = (parent || '').split('/').map(encodeURIComponent).join('/');
        return `/datagroups/${encoded}/subgroups/${encodedParent}/${entity}/${encodeURIComponent(name)}?list=${list}`;
      },
      transformResponse: normalizeSchemaVersions,
      providesTags: (result, error, { name, list = 'releaseVersion' }) => [
        { type: 'ReleaseVersions', id: `${name}-${list}` },
      ],
    }),

    // ── FHIR IG: list publish versions for one guide ─────────────────
    // Iframe-domain groups (e.g. Clinical) present FHIR Implementation
    // Guides. This lists every published version of a guide, newest-first,
    // to populate the version selector. `urlPathSegment` comes from the
    // group config (backend `rt_config.url_path_segment`, e.g. 'fhir_ig').
    getFhirIgVersions: builder.query({
      query: ({ groupName, subGroupChain, guide, urlPathSegment = 'fhir_ig' }) => {
        const segments = subGroupChain.map((sg) => encodeURIComponent(sg)).join('/');
        const base = `/datagroups/${encodeURIComponent(groupName)}/subgroups/${segments}`;
        return `${base}/${encodeURIComponent(urlPathSegment)}/${encodeURIComponent(
          guide
        )}?list=publishVersion`;
      },
      transformResponse: normalizeSchemaVersions,
      providesTags: (result, error, { guide }) => [{ type: 'FhirIgVersions', id: guide }],
    }),

    // ── FHIR IG: get one version's artifacts (metadata + igUri) ──────
    // Returns { name, subGroup, version, publishVersion, igUri, ... }.
    // Omitting `version` returns the latest active version; passing a
    // publishVersion returns that specific one.
    getFhirIgArtifacts: builder.query({
      query: ({ groupName, subGroupChain, guide, version, urlPathSegment = 'fhir_ig' }) => {
        const segments = subGroupChain.map((sg) => encodeURIComponent(sg)).join('/');
        const base = `/datagroups/${encodeURIComponent(groupName)}/subgroups/${segments}`;
        const url = `${base}/${encodeURIComponent(urlPathSegment)}/${encodeURIComponent(
          guide
        )}/artifacts`;
        return version ? `${url}?publishVersion=${encodeURIComponent(version)}` : url;
      },
      providesTags: (result, error, { guide, version }) => [
        { type: 'FhirIgArtifacts', id: `${guide}-${version || 'latest'}` },
      ],
    }),

    // ── Get release schema bundle (two-step: signed URL → S3) ────────
    getReleaseSchema: builder.query({
      queryFn: async ({ groupName, releaseVersion }) => {
        try {
          const path = `/datagroups/${encodeURIComponent(groupName)}/release/${releaseVersion}/schema`;
          const apiData = await signedFetch('GET', path, null);

          const schemaUrl = apiData?.url || apiData?.signedUrl || apiData;
          if (!schemaUrl) throw new Error('No schema URL returned');

          const s3Res = await fetch(schemaUrl);
          if (!s3Res.ok) throw new Error('Failed to fetch schema from S3');

          const schemaJson = await s3Res.json();
          return { data: schemaJson };
        } catch (err) {
          return { error: { status: 'FETCH_ERROR', message: err.message } };
        }
      },
      providesTags: (result, error, { releaseVersion }) => [
        { type: 'ReleaseSchema', id: releaseVersion },
      ],
    }),

    // ── Download release schema as file ──────────────────────────────
    downloadSchema: builder.mutation({
      // The full download (signed URL → blob → save) runs here so any failure —
      // including a failed file fetch — surfaces as an error result the caller
      // can show to the user, rather than being swallowed in a side effect.
      queryFn: async ({ groupName, releaseVersion }) => {
        try {
          const path = `/datagroups/${encodeURIComponent(groupName)}/release/${releaseVersion}/schema`;
          const apiData = await signedFetch('GET', path, null);

          const url = apiData?.url || apiData?.signedUrl || apiData;
          if (!url) throw new Error('No download URL returned');

          const fileRes = await fetch(url);
          if (!fileRes.ok) throw new Error('Failed to download schema file');
          const blob = await fileRes.blob();
          const filename = `${releaseVersion.replace(/_/g, '.')}.schema.json`;
          saveAs(blob, filename);

          return { data: { releaseVersion } };
        } catch (err) {
          // Preserve HTTP status (e.g. 410 Gone for deprecated releases) so the
          // UI can show a meaningful message.
          console.error('[Download] schema download failed', err);
          return { error: { status: err.status || 'DOWNLOAD_ERROR', message: err.message } };
        }
      },
    }),
  }),
});

export const {
  useGetDataGroupsQuery,
  useGetReleasesQuery,
  useLazyGetReleasesQuery,
  useDeprecateReleaseMutation,
  useHardDeleteReleaseMutation,
  useLazyGetGroupDataQuery,
  useGetUntaggedQuery,
  useCreateReleaseMutation,
  useLazyGetEntitySchemaQuery,
  useLazyGetReleaseVersionsForEntityQuery,
  useLazyGetFhirIgVersionsQuery,
  useLazyGetFhirIgArtifactsQuery,
  useGetReleaseSchemaQuery,
  useDownloadSchemaMutation,
} = datagroupsApi;
